import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { NAV } from "@/lib/data";
import { useSiteSettings } from "@/lib/content";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Menu, X } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

// Top-level nav (excludes "Home" — the wordmark is the home link). Memoized
// at module scope so it isn't recomputed on every render of SiteHeader.
const TOP_NAV = NAV.filter((n) => n.to !== "/");

const SiteHeader = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname, hash } = useLocation();
    const SITE = useSiteSettings();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        if (hash) {
            const id = hash.replace("#", "");
            const tryScroll = () => {
                const el = document.getElementById(id);
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                    return true;
                }
                return false;
            };
            if (!tryScroll()) setTimeout(tryScroll, 250);
        } else {
            window.scrollTo({ top: 0, behavior: "instant" });
        }
    }, [pathname, hash]);

    const isAdmin = pathname.startsWith("/admin");
    if (isAdmin) return null;

    // /inquire is a focused conversion page. Strip the full nav and render a
    // minimal "focused-page" header: just the wordmark (escape hatch back
    // home) and a close affordance (returns to the page the visitor came
    // from, or home if they landed here directly).
    if (pathname === "/inquire") {
        return <FocusedPageHeader />;
    }

    const isHome = pathname === "/";
    const isBusiness = pathname === "/business" || pathname.startsWith("/business/");
    const overHero = (isHome || isBusiness) && !scrolled;

    return (
        <header
            data-testid="site-nav"
            className={cn(
                "fixed inset-x-0 top-0 z-40 transition-colors duration-300",
                scrolled ? "bg-[hsl(var(--background))]/85 backdrop-blur-md border-b border-border" : "bg-transparent"
            )}
        >
            <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between">
                <Link to="/" data-testid="site-wordmark" className={cn("font-serif-display text-base sm:text-lg leading-none transition-colors tracking-[0.01em]", overHero ? "text-[hsl(var(--lux-porcelain))]" : "")}>
                    Elizaveta <span className="text-[hsl(var(--lux-brass))]">von</span> Stuben <span className={cn("font-sans text-[10px] tracking-[0.3em] uppercase ml-2 align-middle", overHero ? "text-[hsl(var(--lux-porcelain))]/65" : "text-muted-foreground")}>Imagery</span>
                </Link>

                <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
                    {TOP_NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`nav-link-${n.to.replace("/", "") || "home"}`}
                            className={({ isActive }) =>
                                cn(
                                    "text-[11px] xl:text-[12px] tracking-[0.16em] uppercase lux-underline transition-colors whitespace-nowrap",
                                    overHero
                                        ? "text-[hsl(var(--lux-porcelain))]/85 hover:text-[hsl(var(--lux-porcelain))]"
                                        : isActive ? "text-foreground" : "text-[hsl(var(--muted-foreground))] hover:text-foreground"
                                )
                            }
                        >
                            {n.short || n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2 sm:gap-3">
                    <Link
                        to="/inquire"
                        data-testid="nav-inquire-button"
                        className={cn("!hidden md:!inline-flex btn-request btn-request--sm", overHero && "shadow-[0_8px_30px_rgba(0,0,0,0.18)]")}
                    >
                        Request Availability
                    </Link>
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                data-testid="nav-mobile-menu-button"
                                className={cn("lg:hidden p-2 -mr-2 transition-colors", overHero ? "text-[hsl(var(--lux-porcelain))]" : "text-foreground")}
                                aria-label="Open menu"
                            >
                                <Menu className="h-5 w-5" />
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            side="right"
                            data-testid="mobile-nav-sheet"
                            className="w-[88vw] sm:w-[420px] bg-[hsl(var(--lux-porcelain))]"
                        >
                            <div className="mt-10 flex flex-col gap-3">
                                <div className="kicker mb-2">Navigation</div>
                                {NAV.map((n) => (
                                    <SheetClose asChild key={n.to}>
                                        <NavLink
                                            to={n.to}
                                            data-testid={`mobile-nav-link-${n.to.replace("/", "") || "home"}`}
                                            className={({ isActive }) =>
                                                cn(
                                                    "font-serif-display text-2xl py-1",
                                                    isActive
                                                        ? "text-foreground"
                                                        : "text-foreground/70 hover:text-foreground"
                                                )
                                            }
                                        >
                                            {n.label}
                                        </NavLink>
                                    </SheetClose>
                                ))}
                                <div className="hairline my-6" />
                                <SheetClose asChild>
                                    <Link
                                        to="/inquire"
                                        data-testid="mobile-nav-inquire-button"
                                        className="btn-request w-full justify-center"
                                    >
                                        Request Availability
                                    </Link>
                                </SheetClose>
                                <p className="text-xs text-muted-foreground mt-4">
                                    {SITE.availabilityNote}
                                </p>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};

// Minimal header for focused conversion pages (currently /inquire). Just the
// brand wordmark on the left (so a visitor can leave the form if they need to)
// and a close affordance on the right that returns them to where they came
// from. Pares back competing CTAs to keep focus on the inquiry form.
const FocusedPageHeader = () => {
    const navigate = useNavigate();
    const goBack = () => {
        // If the visitor arrived via an internal CTA, history.length > 1 and
        // navigate(-1) returns them to that page (preserving scroll). For
        // direct landings (paid ads, shared links, typed URL), there's no
        // sensible "back" so we fall home instead.
        if (typeof window !== "undefined" && window.history.length > 1) {
            navigate(-1);
        } else {
            navigate("/");
        }
    };

    return (
        <header
            data-testid="site-nav-focused"
            className="fixed inset-x-0 top-0 z-40 bg-[hsl(var(--background))]/85 backdrop-blur-md border-b border-border"
        >
            <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="focused-nav-wordmark"
                    className="font-serif-display text-base sm:text-lg leading-none tracking-[0.01em]"
                >
                    Elizaveta <span className="text-[hsl(var(--lux-brass))]">von</span> Stuben{" "}
                    <span className="font-sans text-[10px] tracking-[0.3em] uppercase ml-2 align-middle text-muted-foreground">
                        Imagery
                    </span>
                </Link>
                <button
                    type="button"
                    onClick={goBack}
                    data-testid="focused-nav-close"
                    aria-label="Close and return"
                    className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-muted-foreground hover:text-foreground transition-colors p-2 -mr-2"
                >
                    <span className="hidden sm:inline">Close</span>
                    <X className="h-5 w-5" />
                </button>
            </div>
        </header>
    );
};

const SiteFooter = () => {
    const { pathname } = useLocation();
    const SITE = useSiteSettings();
    if (pathname.startsWith("/admin")) return null;
    return (
        <footer className="mt-20 border-t border-border bg-[hsl(var(--lux-porcelain))]">
            <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-5">
                    <div className="font-serif-display text-2xl leading-tight" data-testid="footer-wordmark">Elizaveta von Stuben Imagery</div>
                    <p className="mt-4 text-sm text-muted-foreground max-w-[46ch]" data-testid="footer-brand-description">
                        {SITE.brandDescriptionShort}
                    </p>
                    <div className="hairline-brass mt-6" />
                    <p className="mt-5 text-xs text-muted-foreground" data-testid="footer-availability-note">
                        {SITE.availabilityNote}
                    </p>
                </div>
                <div className="md:col-span-3">
                    <div className="kicker mb-3">Studio</div>
                    <ul className="space-y-2 text-sm">
                        <li data-testid="footer-city">{SITE.cityShort}</li>
                        <li><a className="lux-underline" href={`mailto:${SITE.email}`} data-testid="footer-email">{SITE.email}</a></li>
                        {SITE.phone && <li data-testid="footer-phone">{SITE.phone}</li>}
                        <li>
                            <a
                                className="lux-underline"
                                href={SITE.instagramUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="footer-instagram"
                                aria-label={`Instagram — ${SITE.instagram}`}
                            >
                                {SITE.instagram}
                            </a>
                        </li>
                    </ul>
                </div>
                <div className="md:col-span-2">
                    <div className="kicker mb-3">The House</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link className="lux-underline" to="/life-legacy">Life &amp; Legacy</Link></li>
                        <li><Link className="lux-underline" to="/brand-business">Brand &amp; Business</Link></li>
                        <li><Link className="lux-underline" to="/atelier">The Atelier</Link></li>
                        <li><Link className="lux-underline" to="/mentoring">Studio School</Link></li>
                    </ul>
                </div>
                <div className="md:col-span-2">
                    <div className="kicker mb-3">More</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link className="lux-underline" to="/portfolio">Portfolio</Link></li>
                        <li><Link className="lux-underline" to="/business" data-testid="footer-business-link">Business Packages</Link></li>
                        <li><Link className="lux-underline" to="/reviews">Reviews</Link></li>
                        <li><Link className="lux-underline" to="/journal">Journal</Link></li>
                        <li><Link className="lux-underline" to="/inquire">Request Availability</Link></li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-border">
                <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-10 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="text-xs text-muted-foreground">
                        © {new Date().getFullYear()} Elizaveta von Stuben Imagery. All work © the photographer.
                    </div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
                        {SITE.cityLong}
                    </div>
                </div>
            </div>
        </footer>
    );
};

// =============================================================
// Mobile-only sticky inquiry CTA (Phase A)
// =============================================================
//
// Visibility rules (per spec):
//   - Renders only on mobile (≤768px) via `md:hidden`
//   - Hidden entirely on /inquire (destination), /admin/*, and 404s
//   - Appears once the visitor scrolls past the first viewport (~60% of
//     `innerHeight`) — keeps it from competing with hero CTAs
//   - Hides when an inquiry form is in viewport — IntersectionObserver
//     watches any element marked `data-sticky-cta-hide`
//
// Per-page label + action:
//   - /business → "CHECK SUMMER AVAILABILITY", smooth-scrolls to the
//     on-page `#inquiry` form
//   - everywhere else → "REQUEST AVAILABILITY", navigates to
//     `/inquire?type=...` with a relevant session-type pre-fill

const STICKY_CTA_RULES = [
    // Order matters — first match wins.
    { match: (p) => p === "/business", label: "Check Summer Availability", scrollTo: "#inquiry", type: null },
    { match: (p) => p === "/life-legacy", label: "Request Availability", scrollTo: null, type: "Story Session" },
    { match: (p) => p === "/brand-business", label: "Request Availability", scrollTo: null, type: "Brand Story Session" },
    { match: (p) => p === "/mentoring", label: "Request Availability", scrollTo: null, type: "Mentoring" },
    { match: (p) => p === "/atelier" || p.startsWith("/atelier/"), label: "Request Availability", scrollTo: null, type: "Fine-Art Prints" },
    // Default — home, /portfolio, /journal, /reviews, /about, and any new
    // content page added later. Sends visitors to /inquire with no
    // pre-filled type so they choose for themselves.
    { match: () => true, label: "Request Availability", scrollTo: null, type: null },
];

const HIDDEN_PATHS = (p) =>
    p === "/inquire" ||
    p.startsWith("/admin");

const StickyMobileCta = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [pastHero, setPastHero] = useState(false);
    const [formInView, setFormInView] = useState(false);

    // 1. Scroll trigger — show CTA once visitor has scrolled past ~60% of
    //    the first viewport. Simple, no per-page marker required, and
    //    matches the "appears after hero" intent on every page.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const threshold = () => window.innerHeight * 0.6;
        const onScroll = () => setPastHero(window.scrollY > threshold());
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, [pathname]);

    // 2. Form-in-viewport trigger — hide CTA whenever any element marked
    //    `data-sticky-cta-hide` is visible. Currently only /business uses
    //    this (its inline inquiry form), but any future on-page form just
    //    needs the same data attribute.
    useEffect(() => {
        if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;
        const targets = Array.from(document.querySelectorAll("[data-sticky-cta-hide]"));
        if (targets.length === 0) {
            setFormInView(false);
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                // Hide whenever ANY watched element is visible at all.
                setFormInView(entries.some((e) => e.isIntersecting));
            },
            { rootMargin: "0px 0px -10% 0px", threshold: 0 },
        );
        targets.forEach((t) => observer.observe(t));
        return () => observer.disconnect();
    }, [pathname]);

    if (HIDDEN_PATHS(pathname)) return null;

    const rule = STICKY_CTA_RULES.find((r) => r.match(pathname)) || STICKY_CTA_RULES[STICKY_CTA_RULES.length - 1];
    const visible = pastHero && !formInView;

    const handleClick = (e) => {
        if (rule.scrollTo) {
            e.preventDefault();
            const target = document.querySelector(rule.scrollTo);
            if (target) {
                target.scrollIntoView({ behavior: "smooth", block: "start" });
                if (window.history?.replaceState) {
                    window.history.replaceState(null, "", rule.scrollTo);
                }
            }
        }
        // Otherwise let the <Link> handle navigation natively.
    };

    const href = rule.scrollTo
        ? rule.scrollTo
        : rule.type
            ? `/inquire?type=${encodeURIComponent(rule.type)}`
            : "/inquire";

    return (
        <div
            data-testid="sticky-mobile-cta"
            aria-hidden={!visible}
            // Mobile-only (md = 768px). Full-width pill anchored to bottom.
            // iOS safe-area handled via env() with a fallback for older
            // browsers. Transition only `transform`+`opacity` per design
            // guidelines (no `transition: all`).
            className={cn(
                "md:hidden fixed inset-x-0 bottom-0 z-40",
                "transition-[opacity,transform] duration-200 ease-out",
                visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-3 pointer-events-none",
            )}
            style={{
                paddingBottom: "max(12px, env(safe-area-inset-bottom))",
                paddingTop: "12px",
                paddingLeft: "16px",
                paddingRight: "16px",
                background: "rgba(251, 248, 244, 0.97)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                borderTop: "1px solid hsl(var(--border))",
                boxShadow: "0 -8px 28px rgba(18, 18, 18, 0.10)",
            }}
        >
            {rule.scrollTo ? (
                <a
                    href={rule.scrollTo}
                    onClick={handleClick}
                    data-testid="sticky-mobile-cta-link"
                    className="btn-request w-full justify-center"
                    style={{ minHeight: "48px" }}
                >
                    {rule.label}
                </a>
            ) : (
                <Link
                    to={href}
                    data-testid="sticky-mobile-cta-link"
                    className="btn-request w-full justify-center"
                    style={{ minHeight: "48px" }}
                    onClick={() => {
                        // Scroll to top after route change so the visitor
                        // lands on the inquiry hero, not wherever they
                        // were on the previous page.
                        if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "instant" });
                    }}
                >
                    {rule.label}
                </Link>
            )}
        </div>
    );
};

export default function Layout() {
    return (
        <div className="min-h-screen flex flex-col">
            <SiteHeader />
            <main className="flex-1 pb-20 md:pb-0">
                <Outlet />
            </main>
            <SiteFooter />
            <StickyMobileCta />
            <Toaster richColors position="top-center" />
        </div>
    );
}
